import React, { useState, useEffect, useRef } from 'react'
import {
  RefreshCw,
  LayoutDashboard,
  Calendar,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Database,
  BarChart3,
  Users,
  BadgeCheck,
} from 'lucide-react'
import styles from './Insights.module.scss'
import MetricCard from '../../components/Insights/MetricCard'
import AdvancedSearch, { Filter } from '../../components/Insights/AdvancedSearch'
import DataTable from '../../components/Insights/DataTable'
import NotificationModal, { NotificationType } from '../../components/Insights/NotificationModal'
import DealerPerformance from '../../components/Insights/DealerPerformance'
import CpoFlow from '../../components/Insights/CpoFlow'
import { config } from '../../config'

interface DashboardData {
  found: boolean
  summary: {
    total: any
    published: any
    failed: any
    pending: any
    resolved: any
    retrying: any
  }
  db_check: {
    verified: any
    mismatch: any
    missing: any
  }
}

// Get local date in YYYY-MM-DD format
const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

interface EntityCounts {
  cars: number
  models: number
  modifications: number
  generations: number
}

interface StatusBreakdown {
  total: number
  http_legacy: { [key: string]: number }
  db_check: { [key: string]: number }
}

type AllEntityBreakdowns = {
  [key: string]: StatusBreakdown
}

const ENTITY_TYPES = ['car', 'dealer', 'model', 'modification', 'generation'] as const
const ENTITY_LABELS: { [key: string]: string } = {
  car: 'Cars',
  dealer: 'Dealers',
  model: 'Models',
  modification: 'Modifications',
  generation: 'Generations',
}

const FILTERS_STORAGE_KEY = 'insights_search_filters'
const TAB_STORAGE_KEY = 'insights_active_tab'

type InsightsTab = 'overview' | 'dealer-performance' | 'cpo'
const INSIGHTS_TABS: InsightsTab[] = ['overview', 'dealer-performance', 'cpo']

const Insights: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InsightsTab>(() => {
    const saved = localStorage.getItem(TAB_STORAGE_KEY) as InsightsTab | null
    return saved && INSIGHTS_TABS.includes(saved) ? saved : 'overview'
  })
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateString())
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [searchFilters, setSearchFilters] = useState<Filter[]>(() => {
    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) return parsed
      }
    } catch (e) {
      console.error('Failed to load filters from localStorage', e)
    }
    return []
  })
  const [tableData, setTableData] = useState<any[]>([])
  const [tableTotal, setTableTotal] = useState(0)
  const [tablePage, setTablePage] = useState(1)
  const [tablePageSize, setTablePageSize] = useState(20)
  const [tableLoading, setTableLoading] = useState(false)
  const [entityCounts, setEntityCounts] = useState<EntityCounts>({
    cars: 0,
    models: 0,
    modifications: 0,
    generations: 0,
  })
  const [showRetryDropdown, setShowRetryDropdown] = useState(false)
  const [showDbCheckDropdown, setShowDbCheckDropdown] = useState(false)
  const [bulkOperating, setBulkOperating] = useState(false)
  const [allEntityBreakdowns, setAllEntityBreakdowns] = useState<AllEntityBreakdowns>({})
  const [statusBreakdownLoading, setStatusBreakdownLoading] = useState(false)
  const [notification, setNotification] = useState<{
    isOpen: boolean
    type: NotificationType
    title: string
    message: string
  }>({ isOpen: false, type: 'info', title: '', message: '' })
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} })

  const retryDropdownRef = useRef<HTMLDivElement>(null)
  const dbCheckDropdownRef = useRef<HTMLDivElement>(null)

  const showNotification = (type: NotificationType, title: string, message: string) => {
    setNotification({ isOpen: true, type, title, message })
  }

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, isOpen: false }))
  }

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm })
  }

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }))
  }

  const datePattern = selectedDate.replace(/-/g, '')

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('date', datePattern)

      const res = await fetch(`${config.MONITOR_API_URL}/insights/summary?${params.toString()}`)
      const data = await res.json()
      setDashboardData(data)
    } catch (e) {
      console.error('Failed to fetch insights', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [selectedDate])

  useEffect(() => {
    localStorage.setItem(TAB_STORAGE_KEY, activeTab)
  }, [activeTab])

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const res = await fetch(`${config.MONITOR_API_URL}/insights/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: datePattern }),
      })

      if (res.status === 202) {
        // Poll for completion in the background
        const poll = setInterval(async () => {
          const check = await fetch(`${config.MONITOR_API_URL}/insights/summary?date=${datePattern}`)
          const data = await check.json()
          if (data.found) {
            setDashboardData(data)
            setGenerating(false)
            clearInterval(poll)
          }
        }, 3000)

        setTimeout(() => {
          clearInterval(poll)
          setGenerating(false)
        }, 60000)

        return
      }

      fetchData()
    } catch (e) {
      console.error('Generation failed', e)
    } finally {
      if (generating) setGenerating(false)
    }
  }

  const fetchTableData = async () => {
    setTableLoading(true)
    try {
      const res = await fetch(`${config.MONITOR_API_URL}/insights/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: datePattern,
          filters: searchFilters,
          from: (tablePage - 1) * tablePageSize,
          size: tablePageSize,
        }),
      })
      const data = await res.json()
      setTableData(data.items || [])
      setTableTotal(data.total || 0)
    } catch (e) {
      console.error('Failed to fetch table data', e)
      setTableData([])
      setTableTotal(0)
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    if (dashboardData?.found) {
      fetchTableData()
    }
  }, [searchFilters, tablePage, tablePageSize, selectedDate, dashboardData?.found])

  const handleAdvancedSearch = (filters: Filter[]) => {
    setSearchFilters(filters)
    setTablePage(1)
  }

  const handleRefresh = () => {
    fetchData()
    if (dashboardData?.found) {
      fetchTableData()
    }
  }

  // Fetch entity counts for bulk operations
  const fetchEntityCounts = async () => {
    try {
      const res = await fetch(`${config.MONITOR_API_URL}/insights/entity-counts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: datePattern, filters: searchFilters }),
      })
      const data = await res.json()
      setEntityCounts(data)
    } catch (e) {
      console.error('Failed to fetch entity counts', e)
    }
  }

  useEffect(() => {
    if (dashboardData?.found) {
      fetchEntityCounts()
    }
  }, [searchFilters, datePattern, dashboardData?.found])

  // Fetch status breakdown for all entity types in parallel
  const fetchAllStatusBreakdowns = async () => {
    setStatusBreakdownLoading(true)
    try {
      const results = await Promise.all(
        ENTITY_TYPES.map(async (entityType) => {
          const res = await fetch(`${config.MONITOR_API_URL}/insights/entity-status-breakdown`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ date: datePattern, filters: searchFilters, entity_type: entityType }),
          })
          const data = await res.json()
          return { entityType, data }
        }),
      )

      const breakdowns: AllEntityBreakdowns = {}
      results.forEach(({ entityType, data }) => {
        breakdowns[entityType] = data
      })
      setAllEntityBreakdowns(breakdowns)
    } catch (e) {
      console.error('Failed to fetch status breakdowns', e)
      setAllEntityBreakdowns({})
    } finally {
      setStatusBreakdownLoading(false)
    }
  }

  useEffect(() => {
    if (dashboardData?.found) {
      fetchAllStatusBreakdowns()
    }
  }, [searchFilters, datePattern, dashboardData?.found])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (retryDropdownRef.current && !retryDropdownRef.current.contains(e.target as Node)) {
        setShowRetryDropdown(false)
      }
      if (dbCheckDropdownRef.current && !dbCheckDropdownRef.current.contains(e.target as Node)) {
        setShowDbCheckDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const executeBulkRetry = async (entityType: string) => {
    setBulkOperating(true)
    setShowRetryDropdown(false)
    try {
      const res = await fetch(`${config.MONITOR_API_URL}/insights/bulk-retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: datePattern,
          entity_type: entityType === 'cars' ? 'car' : entityType.slice(0, -1),
          filters: searchFilters,
        }),
      })
      const data = await res.json()
      if (data.success) {
        showNotification('success', 'Bulk Retry Queued', `Retry has been queued for ${data.count} ${entityType}.`)
      } else {
        showNotification('error', 'Bulk Retry Failed', data.error || 'An error occurred.')
      }
    } catch (e) {
      console.error('Bulk retry failed', e)
      showNotification('error', 'Bulk Retry Failed', 'Failed to queue bulk retry. Please try again.')
    } finally {
      setBulkOperating(false)
    }
  }

  // Retry All: models → generations → modifications → cars (no dealers for push)
  const executeRetryAll = async () => {
    setBulkOperating(true)
    setShowRetryDropdown(false)
    const entityOrder = ['models', 'generations', 'modifications', 'cars']
    const results: { entity: string; success: boolean; count?: number; error?: string }[] = []

    for (const entityType of entityOrder) {
      try {
        const res = await fetch(`${config.MONITOR_API_URL}/insights/bulk-retry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: datePattern,
            entity_type: entityType === 'cars' ? 'car' : entityType.slice(0, -1),
            filters: searchFilters,
          }),
        })
        const data = await res.json()
        results.push({ entity: entityType, success: data.success, count: data.count, error: data.error })
      } catch (e) {
        results.push({ entity: entityType, success: false, error: String(e) })
      }
    }

    const successCount = results.filter((r) => r.success).length
    const totalQueued = results.filter((r) => r.success).reduce((sum, r) => sum + (r.count || 0), 0)

    if (successCount === entityOrder.length) {
      showNotification(
        'success',
        'Retry All Queued',
        `Retry queued for all entity types (${totalQueued} total entities).`,
      )
    } else if (successCount > 0) {
      showNotification(
        'warning',
        'Partial Success',
        `Retry queued for ${successCount}/${entityOrder.length} entity types.`,
      )
    } else {
      showNotification('error', 'Retry All Failed', 'Failed to queue retry for any entity type.')
    }

    setBulkOperating(false)
  }

  const handleRetryAll = () => {
    const totalCount = entityCounts.models + entityCounts.generations + entityCounts.modifications + entityCounts.cars
    setShowRetryDropdown(false)
    showConfirm(
      'Confirm Retry All',
      `Are you sure you want to retry push for ALL entities? (${totalCount} total: models → generations → modifications → cars)`,
      () => {
        closeConfirm()
        executeRetryAll()
      },
    )
  }

  const handleBulkRetry = (entityType: string) => {
    const count = entityCounts[entityType as keyof EntityCounts]
    setShowRetryDropdown(false)
    showConfirm('Confirm Bulk Retry', `Are you sure you want to retry push for ${count} ${entityType}?`, () => {
      closeConfirm()
      executeBulkRetry(entityType)
    })
  }

  const executeBulkDbCheck = async (entityType: string) => {
    setBulkOperating(true)
    setShowDbCheckDropdown(false)
    try {
      const res = await fetch(`${config.MONITOR_API_URL}/insights/bulk-db-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: datePattern,
          entity_type: entityType === 'cars' ? 'car' : entityType.slice(0, -1),
          filters: searchFilters,
        }),
      })
      const data = await res.json()
      if (data.success) {
        showNotification('success', 'Bulk DB Check Queued', `DB check has been queued for ${data.count} ${entityType}.`)
      } else {
        showNotification('error', 'Bulk DB Check Failed', data.error || 'An error occurred.')
      }
    } catch (e) {
      console.error('Bulk DB check failed', e)
      showNotification('error', 'Bulk DB Check Failed', 'Failed to queue bulk DB check. Please try again.')
    } finally {
      setBulkOperating(false)
    }
  }

  // DB Check All: models → dealers → generations → modifications → cars
  const executeDbCheckAll = async () => {
    setBulkOperating(true)
    setShowDbCheckDropdown(false)
    const entityOrder = ['models', 'dealers', 'generations', 'modifications', 'cars']
    const results: { entity: string; success: boolean; count?: number; error?: string }[] = []

    for (const entityType of entityOrder) {
      try {
        const res = await fetch(`${config.MONITOR_API_URL}/insights/bulk-db-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: datePattern,
            entity_type: entityType === 'cars' ? 'car' : entityType.slice(0, -1),
            filters: searchFilters,
          }),
        })
        const data = await res.json()
        results.push({ entity: entityType, success: data.success, count: data.count, error: data.error })
      } catch (e) {
        results.push({ entity: entityType, success: false, error: String(e) })
      }
    }

    const successCount = results.filter((r) => r.success).length
    const totalQueued = results.filter((r) => r.success).reduce((sum, r) => sum + (r.count || 0), 0)

    if (successCount === entityOrder.length) {
      showNotification(
        'success',
        'DB Check All Queued',
        `DB check queued for all entity types (${totalQueued} total entities).`,
      )
    } else if (successCount > 0) {
      showNotification(
        'warning',
        'Partial Success',
        `DB check queued for ${successCount}/${entityOrder.length} entity types.`,
      )
    } else {
      showNotification('error', 'DB Check All Failed', 'Failed to queue DB check for any entity type.')
    }

    setBulkOperating(false)
  }

  const handleDbCheckAll = () => {
    setShowDbCheckDropdown(false)
    showConfirm(
      'Confirm DB Check All',
      `Are you sure you want to DB check ALL entities? (models → dealers → generations → modifications → cars)`,
      () => {
        closeConfirm()
        executeDbCheckAll()
      },
    )
  }

  const handleBulkDbCheck = (entityType: string) => {
    const count = entityCounts[entityType as keyof EntityCounts]
    setShowDbCheckDropdown(false)
    showConfirm('Confirm Bulk DB Check', `Are you sure you want to DB check ${count} ${entityType}?`, () => {
      closeConfirm()
      executeBulkDbCheck(entityType)
    })
  }

  const today = getLocalDateString()

  const goToPrevDay = () => {
    const [year, month, day] = selectedDate.split('-').map(Number)
    const current = new Date(year, month - 1, day)
    current.setDate(current.getDate() - 1)
    setSelectedDate(getLocalDateString(current))
  }

  const goToNextDay = () => {
    const [year, month, day] = selectedDate.split('-').map(Number)
    const current = new Date(year, month - 1, day)
    current.setDate(current.getDate() + 1)
    const next = getLocalDateString(current)
    if (next <= today) {
      setSelectedDate(next)
    }
  }

  const canGoNext = selectedDate < today

  const exportCSV = () => {
    if (tableData.length === 0) {
      alert('No data to export')
      return
    }

    const savedCols = localStorage.getItem('insights_table_columns')
    let columns = [
      { field: 'correlation_id', label: 'Correlation ID' },
      { field: 'vin', label: 'VIN' },
      { field: 'relations.generation.title', label: 'Generation' },
      { field: 'relations.model.make', label: 'Make' },
      { field: 'http_legacy_status', label: 'Status' },
    ]

    if (savedCols) {
      try {
        columns = JSON.parse(savedCols).filter((c: any) => c.display)
      } catch (e) {
        console.error('Failed to parse column config', e)
      }
    }

    const getNestedValue = (obj: any, path: string) => {
      return path.split('.').reduce((acc, part) => acc?.[part], obj)
    }

    let csv = columns.map((c) => c.label).join(',') + '\n'

    tableData.forEach((row) => {
      const values = columns.map((col) => {
        const val = getNestedValue(row, col.field)
        return val ? `"${String(val).replace(/"/g, '""')}"` : ''
      })
      csv += values.join(',') + '\n'
    })

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `insights-${selectedDate}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <LayoutDashboard className={styles.headerIcon} />
          <div>
            <h1>Insights Dashboard</h1>
            <p className={styles.subtitle}>Daily feed performance and data integrity metrics</p>
          </div>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.dateNavigation}>
            <button className={styles.navBtn} onClick={goToPrevDay} title="Previous day">
              <ChevronLeft size={18} />
            </button>
            <div className={styles.dateControl}>
              <Calendar size={16} />
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} max={today} />
            </div>
            <button className={styles.navBtn} onClick={goToNextDay} disabled={!canGoNext} title="Next day">
              <ChevronRight size={18} />
            </button>
          </div>

          <button className={styles.generateBtn} onClick={handleGenerate} disabled={generating}>
            <RefreshCw size={16} className={generating ? styles.spinning : ''} />
            {generating ? 'Generating...' : dashboardData?.found ? 'Re-Generate' : 'Generate'}
          </button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('overview')}>
          <BarChart3 size={16} />
          Overview
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'dealer-performance' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('dealer-performance')}>
          <Users size={16} />
          Dealer Performance
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'cpo' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('cpo')}>
          <BadgeCheck size={16} />
          CPO Flow
        </button>
      </div>

      {activeTab === 'cpo' ? (
        <CpoFlow datePattern={datePattern} />
      ) : activeTab === 'dealer-performance' ? (
        <DealerPerformance datePattern={datePattern} />
      ) : loading ? (
        <div className={styles.loader}>Loading metrics...</div>
      ) : !dashboardData?.found ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <RefreshCw size={48} />
          </div>
          <h3>No Data Generated</h3>
          <p>
            No insights found for <strong>{selectedDate}</strong>. Click &quot;Generate&quot; to build the metrics
            index.
          </p>
        </div>
      ) : (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Feed Processing Status</h2>
            <div className={styles.metricsGrid}>
              <MetricCard
                title="Total Inventory"
                value={dashboardData.summary.total.value}
                delta={dashboardData.summary.total.delta}
                breakdown={dashboardData.summary.total.breakdown}
                type="total"
              />
              <MetricCard
                title="Published"
                value={dashboardData.summary.published.value}
                delta={dashboardData.summary.published.delta}
                breakdown={dashboardData.summary.published.breakdown}
                type="success"
                total={dashboardData.summary.total.value}
              />
              <MetricCard
                title="Failed"
                value={dashboardData.summary.failed.value}
                delta={dashboardData.summary.failed.delta}
                breakdown={dashboardData.summary.failed.breakdown}
                type="error"
                total={dashboardData.summary.total.value}
                invertDelta
              />
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Database Integrity Checks</h2>
            <div className={styles.metricsGrid}>
              <MetricCard
                title="Verified in DB"
                value={dashboardData.db_check.verified.value}
                delta={dashboardData.db_check.verified.delta}
                breakdown={dashboardData.db_check.verified.breakdown}
                type="success"
                total={dashboardData.summary.total.value}
              />
              <MetricCard
                title="Mismatches"
                value={dashboardData.db_check.mismatch.value}
                delta={dashboardData.db_check.mismatch.delta}
                breakdown={dashboardData.db_check.mismatch.breakdown}
                type="warning"
                total={dashboardData.summary.total.value}
                invertDelta
              />
              <MetricCard
                title="Missing from DB"
                value={dashboardData.db_check.missing.value}
                delta={dashboardData.db_check.missing.delta}
                breakdown={dashboardData.db_check.missing.breakdown}
                type="error"
                total={dashboardData.summary.total.value}
                invertDelta
              />
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.searchHeader}>
              <h2 className={styles.sectionTitle}>
                <Search size={18} />
                Deep Dive Search
              </h2>
            </div>

            {/* Entity Status Breakdown - All Types as Rows */}
            <div className={styles.statusBreakdownTable}>
              {statusBreakdownLoading ? (
                <div className={styles.statusLoading}>
                  <div className={styles.miniSpinner} />
                </div>
              ) : (
                ENTITY_TYPES.map((entityType) => {
                  const breakdown = allEntityBreakdowns[entityType] || {
                    total: 0,
                    http_legacy: {},
                    db_check: {},
                  }
                  return (
                    <div key={entityType} className={styles.entityRow}>
                      <div className={styles.entityRowLabel}>{ENTITY_LABELS[entityType]}</div>
                      <div className={styles.entityRowTotal}>
                        <span>{breakdown.total.toLocaleString()}</span>
                      </div>
                      <div className={styles.statusColumn}>
                        <span className={styles.columnLabel}>Push</span>
                        <div className={styles.statusChips}>
                          {Object.keys(breakdown.http_legacy).length > 0 ? (
                            Object.entries(breakdown.http_legacy).map(([status, count]) => (
                              <span
                                key={`${entityType}-http-${status}`}
                                className={`${styles.statusChip} ${styles[status] || ''}`}>
                                <span className={styles.chipLabel}>{status}</span>
                                <span className={styles.chipCount}>{count}</span>
                              </span>
                            ))
                          ) : (
                            <span className={styles.statusChip}>
                              <span className={styles.chipLabel}>-</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.statusColumn}>
                        <span className={styles.columnLabel}>DB</span>
                        <div className={styles.statusChips}>
                          {Object.keys(breakdown.db_check).length > 0 ? (
                            Object.entries(breakdown.db_check).map(([status, count]) => (
                              <span
                                key={`${entityType}-db-${status}`}
                                className={`${styles.statusChip} ${styles[status] || ''}`}>
                                <span className={styles.chipLabel}>{status}</span>
                                <span className={styles.chipCount}>{count}</span>
                              </span>
                            ))
                          ) : (
                            <span className={styles.statusChip}>
                              <span className={styles.chipLabel}>-</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            <AdvancedSearch onSearch={handleAdvancedSearch} datePattern={datePattern} filters={searchFilters} />

            {/* Table Actions Toolbar */}
            <div className={styles.tableToolbar}>
              <div className={styles.toolbarLeft}>
                <span className={styles.resultsCount}>{tableTotal.toLocaleString()} results</span>
              </div>
              <div className={styles.toolbarRight}>
                {/* Bulk Retry Push Dropdown */}
                <div className={styles.bulkActionWrapper} ref={retryDropdownRef}>
                  <button
                    className={styles.bulkActionBtn}
                    onClick={() => setShowRetryDropdown(!showRetryDropdown)}
                    disabled={bulkOperating || tableTotal === 0}>
                    {bulkOperating ? <RefreshCw size={16} className={styles.spinning} /> : <RefreshCw size={16} />}
                    Retry Push
                    <ChevronDown size={14} />
                  </button>
                  {showRetryDropdown && (
                    <div className={styles.bulkDropdown}>
                      <button className={styles.bulkDropdownItem} onClick={handleRetryAll}>
                        <strong>Retry All</strong>
                      </button>
                      <div className={styles.bulkDropdownDivider} />
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkRetry('models')}>
                        Models ({entityCounts.models})
                      </button>
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkRetry('generations')}>
                        Generations ({entityCounts.generations})
                      </button>
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkRetry('modifications')}>
                        Modifications ({entityCounts.modifications})
                      </button>
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkRetry('cars')}>
                        Cars ({entityCounts.cars})
                      </button>
                    </div>
                  )}
                </div>

                {/* Bulk DB Check Dropdown */}
                <div className={styles.bulkActionWrapper} ref={dbCheckDropdownRef}>
                  <button
                    className={styles.bulkActionBtn}
                    onClick={() => setShowDbCheckDropdown(!showDbCheckDropdown)}
                    disabled={bulkOperating || tableTotal === 0}>
                    <Database size={16} />
                    DB Check
                    <ChevronDown size={14} />
                  </button>
                  {showDbCheckDropdown && (
                    <div className={styles.bulkDropdown}>
                      <button className={styles.bulkDropdownItem} onClick={handleDbCheckAll}>
                        <strong>Check All</strong>
                      </button>
                      <div className={styles.bulkDropdownDivider} />
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkDbCheck('models')}>
                        Models ({entityCounts.models})
                      </button>
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkDbCheck('dealers')}>
                        Dealers
                      </button>
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkDbCheck('generations')}>
                        Generations ({entityCounts.generations})
                      </button>
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkDbCheck('modifications')}>
                        Modifications ({entityCounts.modifications})
                      </button>
                      <button className={styles.bulkDropdownItem} onClick={() => handleBulkDbCheck('cars')}>
                        Cars ({entityCounts.cars})
                      </button>
                    </div>
                  )}
                </div>

                <button className={styles.exportBtn} onClick={exportCSV} disabled={tableData.length === 0}>
                  <Download size={16} />
                  Export CSV
                </button>
              </div>
            </div>

            <DataTable
              data={tableData}
              total={tableTotal}
              page={tablePage}
              pageSize={tablePageSize}
              onPageChange={setTablePage}
              onPageSizeChange={setTablePageSize}
              loading={tableLoading}
              isToday={selectedDate === today}
              datePattern={datePattern}
            />
          </section>
        </>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={closeNotification}
      />

      {/* Confirm Modal */}
      <NotificationModal
        isOpen={confirmModal.isOpen}
        type="confirm"
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        onClose={closeConfirm}
        isLoading={bulkOperating}
      />
    </div>
  )
}

export default Insights
